/**
 * Display dates as DD/MM/YYYY (and DD/MM/YYYY HH:mm when time is shown).
 * Weekday labels for match rows still use Intl by language.
 */

function parseLocalDateInput(dateInput) {
  if (dateInput == null || dateInput === '') return null;
  const raw = typeof dateInput === 'string' ? dateInput : String(dateInput);
  const d = new Date(raw.includes('T') ? raw : `${raw}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateDMYFromDate(d) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** Plain date: DD/MM/YYYY */
export function formatDateDMY(dateInput) {
  const d = parseLocalDateInput(dateInput);
  return d ? formatDateDMYFromDate(d) : '';
}

/**
 * API / input[type=date] storage: YYYY-MM-DD → display DD/MM/YYYY in forms.
 */
export function isoDateToDMYDisplay(iso) {
  if (iso == null || iso === '') return '';
  const datePart = String(iso).slice(0, 10);
  const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/**
 * User-typed DD/MM/YYYY → YYYY-MM-DD for API, or null if invalid.
 */
export function dmyDisplayToIsoDate(display) {
  const m = String(display)
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const DATE_INPUT_MAX_DIGITS = 8;

/** Strip non-digits and cap length for ddmmyyyy entry. */
export function digitsFromDateInput(value) {
  return String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, DATE_INPUT_MAX_DIGITS);
}

/** Build dd/mm/yyyy with slashes as the user types digits only. */
export function maskDigitsToDMYDisplay(digits) {
  const d = String(digits).slice(0, DATE_INPUT_MAX_DIGITS);
  if (!d) return '';
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * CSV / paste: dd/mm/yyyy (preferred), or legacy yyyy-mm-dd.
 */
export function parseGameDateForCsv(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, mo, d] = s.split('-').map((x) => parseInt(x, 10));
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
    return s;
  }
  const iso = dmyDisplayToIsoDate(s);
  if (iso) return iso;
  const dot = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dot) return dmyDisplayToIsoDate(`${dot[1]}/${dot[2]}/${dot[3]}`);
  return null;
}

/** ISO / createdAt style: DD/MM/YYYY HH:mm (24h, local) */
export function formatIsoDateTimeDMY(isoString) {
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${formatDateDMYFromDate(d)} ${hh}:${mi}`;
}

export function formatMatchDateLabel(dateInput, language) {
  const d = parseLocalDateInput(dateInput);
  if (!d) return '';
  const locale = language === 'en' ? 'en-GB' : 'he-IL';
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d);
  return `${weekday} ${formatDateDMYFromDate(d)}`;
}

/** Date + kickoff time (dashboard upcoming cards). Hour is Israel wall-clock as stored. */
export function formatMatchDateTimeLabel(dateStr, hourStr, language) {
  if (dateStr == null || dateStr === '') return '';
  const h = (hourStr && String(hourStr).trim()) || '12:00';
  const timePart = h.length >= 5 ? h.slice(0, 5) : h;
  const d = parseLocalDateInput(String(dateStr).slice(0, 10));
  if (!d) return timePart;
  const locale = language === 'en' ? 'en-GB' : 'he-IL';
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d);
  return `${weekday} ${formatDateDMYFromDate(d)} · ${timePart}`;
}
